import { useCallback, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { ActionSelections } from '../../core/types/chat.types'
import { ExploreCatalogCard } from '../../core/types/explore.types'
import ExploreMonthYearFields from './parameter-fields/explore-month-year-fields'
import ExploreYearRangeFields from './parameter-fields/explore-year-range-fields'

type ExploreCardInitiatorDialogProps = {
  card: ExploreCatalogCard
  initialSelections?: Partial<ActionSelections>
  onCancel: () => void
  onApply: (selections: Partial<ActionSelections>) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    background: 'var(--ds-bg-surface, #fff)',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    borderRadius: 12,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  cancelButton: {
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 500,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    color: 'var(--ds-fg-primary, #1f2430)',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    '&:hover': { background: 'var(--ds-bg-subtle-hover, #e7eaf1)' },
  },
  applyButton: {
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 500,
    border: 0,
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    color: 'var(--ds-fg-primary, #1f2430)',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    '&:hover': { background: 'var(--ds-bg-subtle-hover, #e7eaf1)' },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
}))

const ExploreCardInitiatorDialog = ({
  card,
  initialSelections,
  onCancel,
  onApply,
}: ExploreCardInitiatorDialogProps) => {
  const classes = useStyles()
  const [isValid, setIsValid] = useState(false)
  const [currentSelections, setCurrentSelections] = useState<Partial<ActionSelections>>({})

  const handleFieldChange = useCallback(
    (selections: Partial<ActionSelections>, valid: boolean) => {
      setCurrentSelections(selections)
      setIsValid(valid)
    },
    []
  )

  const handleApply = () => {
    if (!isValid) return
    onApply(currentSelections)
  }

  const condition = card.initiation.requiredCondition

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <h4 className={classes.title}>{card.title}</h4>
      </div>

      {condition === 'month_and_year' ? (
        <ExploreMonthYearFields
          initialMonth={initialSelections?.monthChosen}
          initialYear={initialSelections?.yearChosen}
          onChange={handleFieldChange}
        />
      ) : condition === 'year1_and_year2' ? (
        <ExploreYearRangeFields
          initialYear1={initialSelections?.year1}
          initialYear2={initialSelections?.year2}
          onChange={handleFieldChange}
        />
      ) : null}

      <div className={classes.actions}>
        <button type="button" className={classes.cancelButton} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={classes.applyButton}
          disabled={!isValid}
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  )
}

export default ExploreCardInitiatorDialog
